import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    Typography,
    IconButton,
    InputAdornment,
    FormHelperText,
    CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { forgotPassword, confirmForgotPassword } from '../queries/forgotpassword';
import PersistentLink from './PersistentLink';

const ForgotPasswordContainer = styled(Container)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '16px',
});

const ForgotPasswordCard = styled(Paper)({
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: '8px',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
});

type Step = 'email' | 'verify' | 'success';

const ForgotPassword: React.FC = () => {
    const location = useLocation();
    const initialStateEmail = location.state?.email || '';

    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState(initialStateEmail);
    const [verificationCode, setVerificationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSendCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await forgotPassword(email);
            if (result.success) {
                setStep('verify');
            } else {
                setError(result.error || 'Failed to send verification code');
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setIsLoading(true);

        try {
            const result = await confirmForgotPassword(email, verificationCode, newPassword);
            if (result.success) {
                setStep('success');
            } else {
                setError(result.error || 'Failed to reset password');
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const renderEmailStep = () => (
        <Box
            component="form"
            onSubmit={handleSendCode}
            sx={{ width: '100%' }}
        >
            <Typography
                variant="body1"
                sx={{
                    mb: 3,
                    mt: 3,
                    color: '#4a5568',
                    textAlign: 'center'
                }}
            >
                {initialStateEmail
                    ? "We'll send a verification code to this email to enable a password reset."
                    : "Enter your email address and we'll send you a verification code to reset your password."
                }
            </Typography>

            <TextField
                fullWidth
                id="email"
                label="Email address"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                }}
                required
                autoFocus={!initialStateEmail}
                disabled={!!initialStateEmail}
                InputLabelProps={{ required: false }}
                error={!!error}
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: error ? '#f56565 !important' : '#e2e8f0',
                        },
                        '&:hover fieldset': {
                            borderColor: error ? '#f56565 !important' : '#cbd5e0',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: error ? '#f56565 !important' : '#4299e1',
                        },
                    },
                    '& .MuiInputLabel-root.Mui-error': {
                        color: '#f56565',
                    },
                    '& .MuiFormHelperText-root.Mui-error': {
                        color: '#f56565',
                        fontWeight: 500,
                    }
                }}
            />

            {error && (
                <FormHelperText
                    error
                    sx={{
                        ml: 1.5,
                        mt: -0.5,
                        mb: 1.5,
                        fontSize: '0.8rem',
                        fontWeight: 500
                    }}
                >
                    {error}
                </FormHelperText>
            )}

            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                    mt: 0,
                    mb: 2,
                    py: 1.5,
                    bgcolor: '#4299e1',
                    '&:hover': {
                        bgcolor: '#2c5282',
                    },
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: 'none',
                    borderRadius: '4px',
                }}
            >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Code'}
            </Button>

            {initialStateEmail && (
                <PersistentLink
                    to="/login"
                    style={{ textDecoration: 'none', width: '100%', display: 'block' }}
                >
                    <Button
                        fullWidth
                        variant="outlined"
                        sx={{
                            mb: 2,
                            py: 1.5,
                            color: '#718096',
                            borderColor: '#e2e8f0',
                            backgroundColor: '#f7fafc',
                            '&:hover': {
                                backgroundColor: '#edf2f7',
                                borderColor: '#cbd5e0',
                            },
                            fontWeight: 500,
                            textTransform: 'none',
                            fontSize: '1rem',
                            boxShadow: 'none',
                            borderRadius: '4px',
                        }}
                    >
                        Log in with a different email
                    </Button>
                </PersistentLink>
            )}

            <Typography variant="body1" sx={{ mt: 3, mb: 4, color: '#4a5568', textAlign: 'center' }}>
                Remember your password?{' '}
                <PersistentLink
                    to="/login"
                    state={{ email: email || initialStateEmail, isPasswordStep: !!(email || initialStateEmail) }}
                    style={{ textDecoration: 'none' }}
                >
                    <Typography
                        variant="inherit"
                        component="span"
                        sx={{
                            color: '#4299e1',
                            fontWeight: 500,
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        Login
                    </Typography>
                </PersistentLink>
            </Typography>
        </Box>
    );

    const renderVerifyStep = () => (
        <Box
            component="form"
            onSubmit={handleResetPassword}
            sx={{ width: '100%' }}
        >
            <Typography
                variant="body1"
                sx={{
                    mb: 3,
                    mt: 3,
                    color: '#4a5568',
                    textAlign: 'center'
                }}
            >
                We've sent a verification code to <strong>{email}</strong>. Enter the code and a new password below.
            </Typography>

            <TextField
                fullWidth
                id="verificationCode"
                label="Verification Code"
                variant="outlined"
                type="text"
                value={verificationCode}
                onChange={(e) => {
                    setVerificationCode(e.target.value);
                    if (error) setError('');
                }}
                required
                autoFocus
                InputLabelProps={{ required: false }}
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: '#e2e8f0',
                        },
                        '&:hover fieldset': {
                            borderColor: '#cbd5e0',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#4299e1',
                        },
                    },
                }}
            />

            <TextField
                fullWidth
                id="newPassword"
                label="New Password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (error) setError('');
                }}
                required
                InputLabelProps={{ required: false }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handlePasswordVisibility}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: error ? '#f56565 !important' : '#e2e8f0',
                        },
                        '&:hover fieldset': {
                            borderColor: error ? '#f56565 !important' : '#cbd5e0',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: error ? '#f56565 !important' : '#4299e1',
                        },
                    },
                    '& .MuiInputLabel-root.Mui-error': {
                        color: '#f56565',
                    },
                    '& .MuiFormHelperText-root.Mui-error': {
                        color: '#f56565',
                        fontWeight: 500,
                    }
                }}
            />

            <TextField
                fullWidth
                id="confirmPassword"
                label="Confirm New Password"
                variant="outlined"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError('');
                }}
                required
                error={!!error}
                InputLabelProps={{ required: false }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle confirm password visibility"
                                onClick={handleConfirmPasswordVisibility}
                                edge="end"
                            >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    mb: error ? 0.5 : 2,
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor: error ? '#f56565 !important' : '#e2e8f0',
                        },
                        '&:hover fieldset': {
                            borderColor: error ? '#f56565 !important' : '#cbd5e0',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: error ? '#f56565 !important' : '#4299e1',
                        },
                    },
                    '& .MuiInputLabel-root.Mui-error': {
                        color: '#f56565',
                    },
                    '& .MuiFormHelperText-root.Mui-error': {
                        color: '#f56565',
                        fontWeight: 500,
                    }
                }}
            />

            {error && (
                <FormHelperText
                    error
                    sx={{
                        ml: 1.5,
                        mt: -0.5,
                        mb: 1.5,
                        fontSize: '0.8rem',
                        fontWeight: 500
                    }}
                >
                    {error}
                </FormHelperText>
            )}

            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                    mt: 0,
                    mb: 2,
                    py: 1.5,
                    bgcolor: '#4299e1',
                    '&:hover': {
                        bgcolor: '#2c5282',
                    },
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: 'none',
                    borderRadius: '4px',
                }}
            >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>

            <PersistentLink
                to="/login"
                style={{ textDecoration: 'none', width: '100%', display: 'block' }}
            >
                <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                        mt: 0,
                        mb: 2,
                        py: 1.5,
                        color: '#718096',
                        borderColor: '#e2e8f0',
                        backgroundColor: '#f7fafc',
                        '&:hover': {
                            backgroundColor: '#edf2f7',
                            borderColor: '#cbd5e0',
                        },
                        fontWeight: 500,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: 'none',
                        borderRadius: '4px',
                    }}
                >
                    Log in with a different email
                </Button>
            </PersistentLink>
        </Box>
    );

    const renderSuccessStep = () => (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
            <CheckCircleOutlineIcon
                sx={{
                    fontSize: 64,
                    color: '#48bb78',
                    mb: 2
                }}
            />

            <Typography
                variant="body1"
                sx={{
                    mb: 2,
                    mt: 3,
                    color: '#4a5568',
                    fontWeight: 600,
                    textAlign: 'center'
                }}
            >
                Password Reset Successful
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    mb: 4,
                    color: '#4a5568'
                }}
            >
                Your password has been reset successfully. You can now log in with your new password.
            </Typography>

            <PersistentLink
                to="/login"
                style={{ textDecoration: 'none', width: '100%' }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    sx={{
                        py: 1.5,
                        bgcolor: '#4299e1',
                        '&:hover': {
                            bgcolor: '#2c5282',
                        },
                        fontWeight: 500,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: 'none',
                        borderRadius: '4px',
                    }}
                >
                    Back to Login
                </Button>
            </PersistentLink>
        </Box>
    );

    return (
        <ForgotPasswordContainer maxWidth={false}>
            <ForgotPasswordCard elevation={0}>
                {/* PrecisCa AI Logo/Title as a link back to landing page */}
                <PersistentLink
                    to="/"
                    style={{ textDecoration: 'none' }}
                >
                    <Box
                        component="img"
                        src="/ai logo no background.png"
                        alt="Precisca AI"
                        sx={{
                            height: { xs: 48, sm: 64 },
                            width: 'auto',
                            display: 'block',
                            mb: 2,
                            cursor: 'pointer'
                        }}
                    />
                </PersistentLink>



                {step === 'email' && renderEmailStep()}
                {step === 'verify' && renderVerifyStep()}
                {step === 'success' && renderSuccessStep()}
            </ForgotPasswordCard>
        </ForgotPasswordContainer>
    );
};

export default ForgotPassword;
